"use client"

import type React from "react"

import { useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Slider } from "@/components/ui/slider"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Settings, Save, RefreshCw, Shield, Globe, Database, Bell, Zap, AlertTriangle } from "lucide-react"

interface SystemConfig {
  allowThreshold: number
  challengeThreshold: number
  blockThreshold: number
  performanceMode: "ultra_fast" | "balanced" | "paranoid"
  securityMode: "low" | "medium" | "high"
  enableRealTimeMonitoring: boolean
  enableIntelligenceEngine: boolean
  webhookUrl: string
  ipWhitelist: string
  ipBlacklist: string
  rateLimitEnabled: boolean
  rateLimitRequests: number
  rateLimitWindow: number
  enableLogging: boolean
  logLevel: "debug" | "info" | "warning" | "error"
  enableAlerts: boolean
  alertThreshold: number
  alertEmail: string
}

interface SystemConfigContentProps {
  config: SystemConfig
  setConfig: React.Dispatch<React.SetStateAction<SystemConfig>>
}

export function SystemConfigContent({ config, setConfig }: SystemConfigContentProps) {
  const [hasChanges, setHasChanges] = useState(false)
  const [activeTab, setActiveTab] = useState("security")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Safe config with defaults
  const safeConfig: SystemConfig = {
    allowThreshold: config?.allowThreshold ?? 40,
    challengeThreshold: config?.challengeThreshold ?? 75,
    blockThreshold: config?.blockThreshold ?? 100,
    performanceMode: config?.performanceMode ?? "ultra_fast",
    securityMode: config?.securityMode ?? "medium",
    enableRealTimeMonitoring: config?.enableRealTimeMonitoring ?? true,
    enableIntelligenceEngine: config?.enableIntelligenceEngine ?? true,
    webhookUrl: config?.webhookUrl ?? "",
    ipWhitelist: config?.ipWhitelist ?? "",
    ipBlacklist: config?.ipBlacklist ?? "",
    rateLimitEnabled: config?.rateLimitEnabled ?? true,
    rateLimitRequests: config?.rateLimitRequests ?? 100,
    rateLimitWindow: config?.rateLimitWindow ?? 60,
    enableLogging: config?.enableLogging ?? true,
    logLevel: config?.logLevel ?? "info",
    enableAlerts: config?.enableAlerts ?? true,
    alertThreshold: config?.alertThreshold ?? 90,
    alertEmail: config?.alertEmail ?? "",
  }

  // Safe config update with validation
  const updateConfig = useCallback(
    (key: keyof SystemConfig, value: any) => {
      try {
        setError(null)

        // Validate input based on key
        let validatedValue = value

        switch (key) {
          case "allowThreshold":
          case "challengeThreshold":
          case "blockThreshold":
          case "alertThreshold":
            validatedValue = Math.max(0, Math.min(100, Number(value) || 0))
            break
          case "rateLimitRequests":
            validatedValue = Math.max(1, Number(value) || 1)
            break
          case "rateLimitWindow":
            validatedValue = Math.max(10, Number(value) || 10)
            break
          case "webhookUrl":
          case "alertEmail":
            validatedValue = String(value || "").trim()
            break
          case "ipWhitelist":
          case "ipBlacklist":
            validatedValue = String(value || "")
            break
        }

        if (typeof setConfig === "function") {
          setConfig((prev: SystemConfig) => ({ ...prev, [key]: validatedValue }))
          setHasChanges(true)
        }
      } catch (error) {
        console.error("Error updating config:", error)
        setError("Failed to update configuration")
      }
    },
    [setConfig],
  )

  // Safe configuration save
  const saveConfiguration = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000))

      console.log("Saving configuration:", config)
      setHasChanges(false)

      // You could add a toast notification here
    } catch (error) {
      console.error("Error saving configuration:", error)
      setError("Failed to save configuration")
    } finally {
      setIsLoading(false)
    }
  }, [config])

  // Safe reset to defaults
  const resetToDefaults = useCallback(() => {
    try {
      setError(null)
      const defaultConfig: SystemConfig = {
        allowThreshold: 40,
        challengeThreshold: 75,
        blockThreshold: 100,
        performanceMode: "ultra_fast",
        securityMode: "medium",
        enableRealTimeMonitoring: true,
        enableIntelligenceEngine: true,
        webhookUrl: "",
        ipWhitelist: "",
        ipBlacklist: "",
        rateLimitEnabled: true,
        rateLimitRequests: 100,
        rateLimitWindow: 60,
        enableLogging: true,
        logLevel: "info",
        enableAlerts: true,
        alertThreshold: 90,
        alertEmail: "",
      }

      if (typeof setConfig === "function") {
        setConfig(defaultConfig)
        setHasChanges(true)
      }
    } catch (error) {
      console.error("Error resetting to defaults:", error)
      setError("Failed to reset configuration")
    }
  }, [setConfig])

  // Safe tab change handler
  const handleTabChange = useCallback((value: string) => {
    try {
      setActiveTab(value)
      setError(null)
    } catch (error) {
      console.error("Error changing tab:", error)
    }
  }, [])

  return (
    <>
      {error && (
        <Card className="bg-red-900/50 border-red-700/50 backdrop-blur-sm mb-4">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <span className="text-red-300">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration Header */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Settings className="mr-2 h-5 w-5 text-green-500" />
              System Configuration
            </CardTitle>
            <div className="flex items-center space-x-2">
              {hasChanges && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">Unsaved Changes</Badge>
              )}
              <Button variant="outline" size="sm" onClick={resetToDefaults} disabled={isLoading}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Reset
              </Button>
              <Button
                size="sm"
                onClick={saveConfiguration}
                disabled={!hasChanges || isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-1" />
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="bg-slate-800/50 p-1 mb-6">
              <TabsTrigger
                value="security"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Shield className="h-4 w-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger
                value="performance"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Zap className="h-4 w-4 mr-2" />
                Performance
              </TabsTrigger>
              <TabsTrigger
                value="network"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Globe className="h-4 w-4 mr-2" />
                Network
              </TabsTrigger>
              <TabsTrigger
                value="monitoring"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Database className="h-4 w-4 mr-2" />
                Monitoring
              </TabsTrigger>
              <TabsTrigger
                value="alerts"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Bell className="h-4 w-4 mr-2" />
                Alerts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="security" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <h3 className="text-lg font-semibold text-slate-200 mb-4">Security Thresholds</h3>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">
                          Allow Threshold: {safeConfig.allowThreshold}%
                        </Label>
                        <Slider
                          value={[safeConfig.allowThreshold]}
                          onValueChange={(value) => updateConfig("allowThreshold", value[0])}
                          max={100}
                          step={5}
                          className="w-full"
                          disabled={isLoading}
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Requests with scores above this threshold are allowed
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">
                          Challenge Threshold: {safeConfig.challengeThreshold}%
                        </Label>
                        <Slider
                          value={[safeConfig.challengeThreshold]}
                          onValueChange={(value) => updateConfig("challengeThreshold", value[0])}
                          max={100}
                          step={5}
                          className="w-full"
                          disabled={isLoading}
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Requests with scores below this threshold are challenged
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">
                          Block Threshold: {safeConfig.blockThreshold}%
                        </Label>
                        <Slider
                          value={[safeConfig.blockThreshold]}
                          onValueChange={(value) => updateConfig("blockThreshold", value[0])}
                          max={100}
                          step={5}
                          className="w-full"
                          disabled={isLoading}
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Requests with scores below this threshold are blocked
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <h3 className="text-lg font-semibold text-slate-200 mb-4">Security Modes</h3>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">Security Mode</Label>
                        <Select
                          value={safeConfig.securityMode}
                          onValueChange={(value: "low" | "medium" | "high") => updateConfig("securityMode", value)}
                          disabled={isLoading}
                        >
                          <SelectTrigger className="bg-slate-800/50 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low - Fast processing, basic checks</SelectItem>
                            <SelectItem value="medium">Medium - Balanced security and performance</SelectItem>
                            <SelectItem value="high">High - Maximum security, slower processing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm text-slate-300">Real-time Monitoring</Label>
                          <div className="text-xs text-slate-500">Enable live traffic monitoring</div>
                        </div>
                        <Switch
                          checked={safeConfig.enableRealTimeMonitoring}
                          onCheckedChange={(checked) => updateConfig("enableRealTimeMonitoring", checked)}
                          disabled={isLoading}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm text-slate-300">Intelligence Engine</Label>
                          <div className="text-xs text-slate-500">Enable AI-powered threat detection</div>
                        </div>
                        <Switch
                          checked={safeConfig.enableIntelligenceEngine}
                          onCheckedChange={(checked) => updateConfig("enableIntelligenceEngine", checked)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <h3 className="text-lg font-semibold text-slate-200 mb-4">IP Management</h3>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">IP Whitelist</Label>
                        <Textarea
                          placeholder="Enter IP addresses or ranges, one per line..."
                          value={safeConfig.ipWhitelist}
                          onChange={(e) => updateConfig("ipWhitelist", e.target.value)}
                          className="bg-slate-800/50 border-slate-700 font-mono text-sm"
                          rows={4}
                          disabled={isLoading}
                        />
                        <div className="text-xs text-slate-500 mt-1">IPs that always bypass security checks</div>
                      </div>

                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">IP Blacklist</Label>
                        <Textarea
                          placeholder="Enter IP addresses or ranges, one per line..."
                          value={safeConfig.ipBlacklist}
                          onChange={(e) => updateConfig("ipBlacklist", e.target.value)}
                          className="bg-slate-800/50 border-slate-700 font-mono text-sm"
                          rows={4}
                          disabled={isLoading}
                        />
                        <div className="text-xs text-slate-500 mt-1">IPs that are always blocked</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="performance" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Performance Settings</h3>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-300 mb-2 block">Performance Mode</Label>
                      <Select
                        value={safeConfig.performanceMode}
                        onValueChange={(value: "ultra_fast" | "balanced" | "paranoid") =>
                          updateConfig("performanceMode", value)
                        }
                        disabled={isLoading}
                      >
                        <SelectTrigger className="bg-slate-800/50 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ultra_fast">Ultra Fast - Minimal processing</SelectItem>
                          <SelectItem value="balanced">Balanced - Optimal performance/security</SelectItem>
                          <SelectItem value="paranoid">Paranoid - Maximum security checks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm text-slate-300">Rate Limiting</Label>
                        <div className="text-xs text-slate-500">Enable request rate limiting</div>
                      </div>
                      <Switch
                        checked={safeConfig.rateLimitEnabled}
                        onCheckedChange={(checked) => updateConfig("rateLimitEnabled", checked)}
                        disabled={isLoading}
                      />
                    </div>

                    {safeConfig.rateLimitEnabled && (
                      <>
                        <div>
                          <Label className="text-sm text-slate-300 mb-2 block">
                            Requests per Window: {safeConfig.rateLimitRequests}
                          </Label>
                          <Slider
                            value={[safeConfig.rateLimitRequests]}
                            onValueChange={(value) => updateConfig("rateLimitRequests", value[0])}
                            min={10}
                            max={1000}
                            step={10}
                            className="w-full"
                            disabled={isLoading}
                          />
                        </div>

                        <div>
                          <Label className="text-sm text-slate-300 mb-2 block">
                            Time Window: {safeConfig.rateLimitWindow}s
                          </Label>
                          <Slider
                            value={[safeConfig.rateLimitWindow]}
                            onValueChange={(value) => updateConfig("rateLimitWindow", value[0])}
                            min={10}
                            max={300}
                            step={10}
                            className="w-full"
                            disabled={isLoading}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Cache Settings</h3>

                  <div className="space-y-4">
                    <div className="text-sm text-slate-400">
                      Cache settings are automatically optimized based on your performance mode.
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-900/50 rounded p-3 border border-slate-700/30">
                        <div className="text-xs text-slate-500">Cache Hit Rate</div>
                        <div className="text-lg font-bold text-green-400">94.2%</div>
                      </div>
                      <div className="bg-slate-900/50 rounded p-3 border border-slate-700/30">
                        <div className="text-xs text-slate-500">Cache Size</div>
                        <div className="text-lg font-bold text-blue-400">2.1GB</div>
                      </div>
                    </div>

                    <Button variant="outline" className="w-full bg-transparent" disabled={isLoading}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Clear Cache
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="network" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Webhook Configuration</h3>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-300 mb-2 block">Webhook URL</Label>
                      <Input
                        placeholder="https://your-webhook-endpoint.com/webhook"
                        value={safeConfig.webhookUrl}
                        onChange={(e) => updateConfig("webhookUrl", e.target.value)}
                        className="bg-slate-800/50 border-slate-700"
                        disabled={isLoading}
                      />
                      <div className="text-xs text-slate-500 mt-1">
                        Receive real-time notifications about security events
                      </div>
                    </div>

                    <Button variant="outline" className="w-full bg-transparent" disabled={isLoading}>
                      Test Webhook
                    </Button>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Network Status</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">CDN Status</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Edge Locations</span>
                      <span className="text-sm text-slate-300">24 Active</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Global Latency</span>
                      <span className="text-sm text-slate-300">45ms avg</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="monitoring" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Logging Configuration</h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm text-slate-300">Enable Logging</Label>
                        <div className="text-xs text-slate-500">Store system and security logs</div>
                      </div>
                      <Switch
                        checked={safeConfig.enableLogging}
                        onCheckedChange={(checked) => updateConfig("enableLogging", checked)}
                        disabled={isLoading}
                      />
                    </div>

                    {safeConfig.enableLogging && (
                      <div>
                        <Label className="text-sm text-slate-300 mb-2 block">Log Level</Label>
                        <Select
                          value={safeConfig.logLevel}
                          onValueChange={(value: "debug" | "info" | "warning" | "error") =>
                            updateConfig("logLevel", value)
                          }
                          disabled={isLoading}
                        >
                          <SelectTrigger className="bg-slate-800/50 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="debug">Debug - All events</SelectItem>
                            <SelectItem value="info">Info - General information</SelectItem>
                            <SelectItem value="warning">Warning - Important events</SelectItem>
                            <SelectItem value="error">Error - Critical issues only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Storage Usage</h3>

                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Log Storage</span>
                        <span className="text-slate-300">1.2GB / 5GB</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: "24%" }}></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Cache Storage</span>
                        <span className="text-slate-300">2.1GB / 10GB</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: "21%" }}></div>
                      </div>
                    </div>

                    <Button variant="outline" className="w-full bg-transparent" disabled={isLoading}>
                      <Database className="h-4 w-4 mr-2" />
                      Manage Storage
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="alerts" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Alert Configuration</h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm text-slate-300">Enable Alerts</Label>
                        <div className="text-xs text-slate-500">Receive notifications for security events</div>
                      </div>
                      <Switch
                        checked={safeConfig.enableAlerts}
                        onCheckedChange={(checked) => updateConfig("enableAlerts", checked)}
                        disabled={isLoading}
                      />
                    </div>

                    {safeConfig.enableAlerts && (
                      <>
                        <div>
                          <Label className="text-sm text-slate-300 mb-2 block">
                            Alert Threshold: {safeConfig.alertThreshold}%
                          </Label>
                          <Slider
                            value={[safeConfig.alertThreshold]}
                            onValueChange={(value) => updateConfig("alertThreshold", value[0])}
                            max={100}
                            step={5}
                            className="w-full"
                            disabled={isLoading}
                          />
                          <div className="text-xs text-slate-500 mt-1">
                            Trigger alerts when threat score exceeds this threshold
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm text-slate-300 mb-2 block">Alert Email</Label>
                          <Input
                            placeholder="admin@example.com"
                            value={safeConfig.alertEmail}
                            onChange={(e) => updateConfig("alertEmail", e.target.value)}
                            className="bg-slate-800/50 border-slate-700"
                            disabled={isLoading}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Recent Alerts</h3>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-2 bg-slate-900/50 rounded border border-red-500/30">
                      <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm text-slate-200">High bot activity detected</div>
                        <div className="text-xs text-slate-500">2 minutes ago</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-2 bg-slate-900/50 rounded border border-amber-500/30">
                      <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm text-slate-200">Rate limit threshold reached</div>
                        <div className="text-xs text-slate-500">15 minutes ago</div>
                      </div>
                    </div>

                    <div className="text-center text-sm text-slate-500 py-4">No more recent alerts</div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  )
}
